/**
 * Shared queue-screen toolbar + loan-stage join (Appraisal Queue, Lock Queue).
 *
 * Layout every work-queue screen follows above its table:
 *   top    — scope pills (which pipeline / mode) + switches (`ToolbarSwitch`)
 *   middle — the stat widgets (screen-owned)
 *   bottom — `QueueStatusBar`: status pills, "Showing N of M", search
 *
 * Queue rows (appraisal orders, locks) carry only a `dealId`, so the loan's
 * current stage + closing date come from `fetchLoanIndex()` — both pipeline
 * lists joined client-side — and render through `LoanStageCell` /
 * `ClosingDateCell`. `loan.inactive` drives the "Include Dead / Inactive /
 * Closed Loans" switch.
 */
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputBase from "@mui/material/InputBase";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import SearchRounded from "@mui/icons-material/SearchRounded";

import { losAcquisitionsApi } from "../../api/losAcquisitionsApi";
import { losPipelineApi } from "../../api/losPipelineApi";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { selectedStateSx } from "../../theme/activeStates";
import { status as statusTones } from "../../theme/colors";
import { partnerStorage } from "../../utils/storage/partnerStorage";

// Neutral queue palette (same values the queue screens use).
const Q = {
  card: "#ffffff",
  line: "#e3e9ec",
  ink900: "#16242b",
  ink700: "#36474f",
  ink500: "#697680",
  ink300: "#aab4bb",
  teal500: "#21a9c4",
  radius: "12px",
  shadow: "0 1px 2px rgba(16,24,40,0.04)",
};

/* ── loan stage / closing date join ─────────────────────────────────────── */

// Acquisition statuses are the model's exact strings (`acquisition_status_options`).
const ACQ_DEAD = new Set(["Kicked", "Declined"]);
const ACQ_CLOSED = new Set(["Settled", "Boarded"]);

const norm = (v) => String(v || "").trim().toLowerCase();

// Closed = any "Closed…" status (Closed, Closed w/ Post Close Conditions, …),
// the Closed pipeline bucket, or a Funded sub-status (loan_substatus['Closed']).
// Case-insensitive so an off-casing value can't slip a closed loan through.
const isPrecloseClosed = (d) => {
  const s = norm(d?.loanStatus);
  return (
    s.startsWith("closed") ||
    s.startsWith("funded") ||
    norm(d?.pipeline) === "closed" ||
    norm(d?.subStatus).startsWith("funded")
  );
};
const isPrecloseDead = (d) =>
  ["dead", "inactive"].includes(norm(d?.loanStatus));

/** ObjectIds arrive as strings or `{ $oid }` — normalize to a string key. */
export const dealKey = (v) =>
  v && typeof v === "object" ? String(v.$oid || v._id || "") : String(v || "");

/**
 * `{ [dealId]: { loanNumber, loanStatus, originator, isAcquisition, inactive,
 * dead, closingDate, closingBasis } }` from the two pipeline lists.
 * `originator` is the correspondent company on both pipelines.
 */
export const buildLoanIndex = (uwRows, acqRows) => {
  const index = {};
  (Array.isArray(uwRows) ? uwRows : []).forEach((d) => {
    if (!d?._id) return;
    const closed = isPrecloseClosed(d);
    const dead = isPrecloseDead(d);
    // A closed loan's closing date is its note date; a live one's the estimate.
    const closingDate = closed
      ? d.originationDate || d.expectedClosingDate
      : d.expectedClosingDate;
    index[dealKey(d._id)] = {
      loanNumber: d.loanNumber ?? null,
      loanStatus: d.loanStatus || null,
      originator: d.originator || null,
      isAcquisition: false,
      inactive: closed || dead,
      dead,
      closingDate: closingDate || null,
      closingBasis: closingDate
        ? closed && d.originationDate
          ? "closed"
          : "expected"
        : null,
    };
  });
  (Array.isArray(acqRows) ? acqRows : []).forEach((d) => {
    if (!d?._id) return;
    const s = d.loanStatus || "";
    // Acquisitions schedule a trade SETTLEMENT, not a closing — show that when
    // present, else the note date the loan closed on.
    const closingDate = d.settlementDate || d.originationDate || null;
    index[dealKey(d._id)] = {
      loanNumber: d.loanNumber ?? null,
      loanStatus: s || null,
      originator: d.originator || null,
      isAcquisition: true,
      inactive:
        ACQ_DEAD.has(s) || ACQ_CLOSED.has(s) || norm(d.pipeline) === "boarded",
      dead: ACQ_DEAD.has(s),
      closingDate,
      closingBasis: d.settlementDate
        ? "settlement"
        : d.originationDate
          ? "closed"
          : null,
    };
  });
  return index;
};

/**
 * Fetch both pipelines and build the index. Resolves `null` when both fail —
 * callers must then hide nothing (an unknown stage is never "inactive").
 */
export const fetchLoanIndex = () =>
  Promise.allSettled([
    losPipelineApi.getUnderwritingPipeline(),
    losAcquisitionsApi.getAcquisitionsPipeline(),
  ]).then(([uw, acq]) => {
    if (uw.status === "rejected" && acq.status === "rejected") {
      console.error("Loan stage join: pipeline fetch failed", uw.reason);
      return null;
    }
    return buildLoanIndex(
      uw.status === "fulfilled" ? uw.value : [],
      acq.status === "fulfilled" ? acq.value : [],
    );
  });

/** Stamp a queue row with its loan's stage fields (see `buildLoanIndex`). */
export const withLoanStage = (row, loanIndex, fallbackIsAcquisition = false) => {
  const loan = loanIndex?.[dealKey(row?.dealId)];
  return {
    ...row,
    loanStage: loan?.loanStatus || null,
    loanIsAcquisition: loan?.isAcquisition ?? fallbackIsAcquisition,
    loanInactive: Boolean(loan?.inactive),
    loanClosingDate: loan?.closingDate || null,
    loanClosingBasis: loan?.closingBasis || null,
    _loan: loan || null,
  };
};

export const INACTIVE_TOOLTIP =
  "Dead, Inactive and Closed loans (including Closed w/ Post Close Conditions and Funded; acquisitions: Kicked, Declined, Settled or Boarded) are hidden unless this is on.";

/** Persisted (partner-scoped) "hide inactive loans" preference, default true. */
export const readHideInactive = (key) => {
  try {
    return partnerStorage.getItem(key) !== "false";
  } catch {
    return true;
  }
};
export const writeHideInactive = (key, value) => {
  try {
    partnerStorage.setItem(key, String(value));
  } catch {
    // storage unavailable — the switch still works for this session
  }
};

const stageTone = (loan) => {
  const s = loan?.loanStatus;
  if (!s) return statusTones.neutral;
  if (loan.dead) return statusTones.error;
  if (loan.inactive) return statusTones.success; // closed / settled / boarded
  if (s === "Clear To Close" || s === "Cleared to Purchase")
    return statusTones.warning;
  return statusTones.info;
};

const CLOSING_BASIS_LABEL = {
  expected: "expected",
  closed: "closed",
  settlement: "settlement",
};

/**
 * AG Grid date-filter comparator for date-only / ISO strings. Compares on the
 * string's calendar fields (not `new Date(str)`), so a date can't slip a day
 * in a negative-offset zone.
 */
export const dateOnlyComparator = (filterDate, cellValue) => {
  const m = String(cellValue || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return -1;
  const cell = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const f = new Date(
    filterDate.getFullYear(),
    filterDate.getMonth(),
    filterDate.getDate(),
  );
  if (cell < f) return -1;
  if (cell > f) return 1;
  return 0;
};

/** Cell: the loan's stage pill (+ "Acquisition" under it). Field `loanStage`. */
export const LoanStageCell = ({ value, data, loaded = true }) => {
  if (!value) {
    return (
      <Box
        component="span"
        sx={{ color: Q.ink300 }}
        title={loaded ? "Loan not found in either pipeline" : "Pipeline not loaded"}
      >
        —
      </Box>
    );
  }
  const tone = stageTone(data?._loan);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        height: "100%",
        lineHeight: 1.25,
        gap: "2px",
      }}
    >
      <Box
        component="span"
        sx={{
          alignSelf: "flex-start",
          px: "8px",
          py: "2px",
          borderRadius: "999px",
          fontSize: 11.5,
          fontWeight: 600,
          whiteSpace: "nowrap",
          bgcolor: tone.bg,
          color: tone.fg,
        }}
      >
        {value}
      </Box>
      {data?.loanIsAcquisition && (
        <Box component="span" sx={{ fontSize: 11, color: Q.ink500 }}>
          Acquisition
        </Box>
      )}
    </Box>
  );
};

/** Cell: closing date + what kind of date it is. Field `loanClosingDate`. */
export const ClosingDateCell = ({ value, data }) => {
  if (!value)
    return (
      <Box component="span" sx={{ color: Q.ink300 }}>
        —
      </Box>
    );
  const basis = CLOSING_BASIS_LABEL[data?.loanClosingBasis];
  return (
    <Box sx={{ lineHeight: 1.25 }}>
      <Box sx={{ color: Q.ink900, fontSize: 13 }}>{formatDate(value, "")}</Box>
      {basis && <Box sx={{ fontSize: 11.5, color: Q.ink500 }}>{basis}</Box>}
    </Box>
  );
};

/** The Loan stage + Closing date column defs, with floating filters. */
export const loanStageColumnDefs = ({ loaded = true } = {}) => [
  {
    headerName: "Loan stage",
    field: "loanStage",
    width: 170,
    filter: "agSetColumnFilter",
    filterParams: { valueFormatter: (p) => p.value || "(Not found)" },
    cellRenderer: (p) => (
      <LoanStageCell value={p.value} data={p.data} loaded={loaded} />
    ),
  },
  {
    headerName: "Closing date",
    field: "loanClosingDate",
    width: 150,
    filter: "agDateColumnFilter",
    filterParams: { comparator: dateOnlyComparator },
    cellRenderer: (p) => <ClosingDateCell value={p.value} data={p.data} />,
  },
];

/* ── toolbar pieces ─────────────────────────────────────────────────────── */

/**
 * A row of pills with counts. Used for the scope row (top) and the status row
 * (bottom). Both change the DATA, not the page → level-2 selected state.
 * Options: `{ key, label, dot? }`; omit `counts` to hide the numbers.
 */
export const PillGroup = ({ options, value, onChange, counts }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
    {options.map(({ key, label, dot }) => {
      const on = value === key;
      return (
        <Box
          key={key}
          component="button"
          type="button"
          aria-pressed={on}
          onClick={() => onChange(key)}
          sx={{
            font: "inherit",
            fontSize: 13,
            fontWeight: 600,
            color: Q.ink700,
            background: "#fff",
            border: `1px solid ${Q.line}`,
            borderRadius: "999px",
            px: "12px",
            py: "6px",
            cursor: "pointer",
            display: "inline-flex",
            gap: "7px",
            alignItems: "center",
            whiteSpace: "nowrap",
            "&:hover": { background: "#f7f9fa" },
            "&:focus-visible": {
              outline: `2px solid ${Q.teal500}`,
              outlineOffset: 2,
            },
            ...(on && selectedStateSx({ level: 2, variant: "circle" })),
          }}
        >
          {dot && (
            <Box
              component="span"
              sx={{ width: 7, height: 7, borderRadius: "50%", background: dot }}
            />
          )}
          {label}
          {counts && (
            <Box
              component="span"
              sx={{ fontSize: 11.5, fontWeight: 600, color: Q.ink500 }}
            >
              {counts[key] ?? 0}
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

/** A labelled switch for the top row ("My Queue", "Include Dead / …"). */
export const ToolbarSwitch = ({ checked, onChange, label, note, tooltip }) => {
  const control = (
    <FormControlLabel
      sx={{ ml: 0.5, mr: 0 }}
      control={
        <Switch
          size="small"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
      label={
        <Box component="span" sx={{ fontSize: 13, color: Q.ink700 }}>
          {label}
          {note ? (
            <Box component="span" sx={{ color: Q.ink500 }}>
              {` ${note}`}
            </Box>
          ) : null}
        </Box>
      }
    />
  );
  return tooltip ? (
    <Tooltip arrow title={tooltip}>
      {control}
    </Tooltip>
  ) : (
    control
  );
};

/** Top row: an optional leading label, then the scope pills + switches. */
export const QueueScopeRow = ({ label, children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      flexWrap: "wrap",
      mt: "22px",
    }}
  >
    {label && (
      <Box sx={{ fontSize: 13, fontWeight: 600, color: Q.ink500 }}>{label}</Box>
    )}
    {children}
  </Box>
);

/** The toolbar search box (compact — matches the pill row's height). */
export const QueueSearch = ({ value, onChange, placeholder, sx }) => (
  <Box
    sx={{ position: "relative", display: "flex", alignItems: "center", ...sx }}
  >
    <SearchRounded
      sx={{
        position: "absolute",
        left: 11,
        fontSize: 18,
        color: Q.ink300,
        zIndex: 1,
        pointerEvents: "none",
      }}
    />
    <InputBase
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || "Search…"}
      inputProps={{ "aria-label": placeholder || "Search" }}
      sx={{
        width: "100%",
        height: 34,
        fontSize: 13,
        pl: "36px",
        pr: "12px",
        border: `1px solid ${Q.line}`,
        borderRadius: "9px",
        background: "#fff",
        "&.Mui-focused": {
          borderColor: Q.teal500,
          boxShadow: "0 0 0 3px rgba(33,169,196,.14)",
        },
      }}
    />
  </Box>
);

/**
 * Bottom row card: status pills on the left (they wrap), "Showing N of M" +
 * search (+ optional `actions`) on one line at the right.
 */
export const QueueStatusBar = ({
  pills,
  shown,
  total,
  search,
  onSearch,
  placeholder,
  actions,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      mb: 1.75,
      flexWrap: "wrap",
      background: Q.card,
      border: `1px solid ${Q.line}`,
      borderRadius: Q.radius,
      boxShadow: Q.shadow,
      p: "12px 16px",
    }}
  >
    <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>{pills}</Box>
    <Box
      sx={{
        ml: "auto",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexShrink: 0,
      }}
    >
      <Box sx={{ fontSize: 13, color: Q.ink500, whiteSpace: "nowrap" }}>
        Showing{" "}
        <Box component="b" sx={{ color: Q.ink900, fontWeight: 700 }}>
          {shown}
        </Box>{" "}
        of {total}
      </Box>
      <QueueSearch
        value={search}
        onChange={onSearch}
        placeholder={placeholder}
        sx={{ width: { xs: 220, md: 280 } }}
      />
      {actions}
    </Box>
  </Box>
);
